export enum UserRole {
  ADMIN = 'ADMIN',
  FLEET_MANAGER = 'FLEET_MANAGER',
  FINANCE_OFFICER = 'FINANCE_OFFICER',
  DRIVER = 'DRIVER',
  AUDITOR = 'AUDITOR',
}

export enum FuelType {
  PETROL = 'PETROL',
  DIESEL = 'DIESEL',
  ELECTRIC = 'ELECTRIC',
  HYBRID = 'HYBRID',
}

export enum TripStatus {
  PLANNED = 'PLANNED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum TransactionType {
  ISSUED = 'ISSUED',
  PURCHASED = 'PURCHASED',
}

export enum AllowanceStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum AllowanceType {
  // Core Driver Allowances
  PER_DIEM = 'PER_DIEM',
  MEAL = 'MEAL',
  NIGHT_OUT = 'NIGHT_OUT',
  OVERTIME = 'OVERTIME',
  TURNBOY = 'TURNBOY',
  LOADING_OFFLOADING = 'LOADING_OFFLOADING',

  // Route-Based Allowances
  LOCAL_TRIP = 'LOCAL_TRIP',
  UPCOUNTRY_TRIP = 'UPCOUNTRY_TRIP',
  CROSS_BORDER = 'CROSS_BORDER',
  REMOTE_AREA = 'REMOTE_AREA',
  RISK_ZONE = 'RISK_ZONE',

  // Operational / Field Expenses
  COMMUNICATION = 'COMMUNICATION',
  PARKING = 'PARKING',
  TOLL_FEES = 'TOLL_FEES',
  WEIGHBRIDGE = 'WEIGHBRIDGE',
  BORDER_PROCESSING = 'BORDER_PROCESSING',

  // Special Cargo Allowances
  REFRIGERATED_CARGO = 'REFRIGERATED_CARGO',
  HAZARDOUS_MATERIAL = 'HAZARDOUS_MATERIAL',
  OVERSIZED_LOAD = 'OVERSIZED_LOAD',
  NIGHT_DRIVING = 'NIGHT_DRIVING',

  // Incentive / Performance Allowances
  ON_TIME_BONUS = 'ON_TIME_BONUS',
  FUEL_EFFICIENCY_BONUS = 'FUEL_EFFICIENCY_BONUS',
  NO_ACCIDENT_BONUS = 'NO_ACCIDENT_BONUS',
  CUSTOMER_SATISFACTION_BONUS = 'CUSTOMER_SATISFACTION_BONUS',
}

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  ISSUED = 'ISSUED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export enum ProFormaStatus {
  DRAFT = 'DRAFT',
  ISSUED = 'ISSUED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export enum PaymentMethod {
  CASH = 'CASH',
  BANK_TRANSFER = 'BANK_TRANSFER',
  MOBILE_MONEY = 'MOBILE_MONEY',
  CHEQUE = 'CHEQUE',
  CREDIT_CARD = 'CREDIT_CARD',
  OTHER = 'OTHER',
}

export enum PaymentStatus {
  RECORDED = 'RECORDED',
  VERIFIED = 'VERIFIED',
  CANCELLED = 'CANCELLED',
}

export interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  active: boolean;
  passwordChangeRequired: boolean;
  roles: UserRole[];
  driverId?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  accessToken: string;
  tokenType: string;
  userId: number;
  username: string;
  passwordChangeRequired: boolean;
  roles: UserRole[];
  driverId?: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface Vehicle {
  id: number;
  registrationNumber: string;
  make: string;
  model: string;
  year: number;
  fuelType: FuelType;
  fuelEfficiencyKmPerLiter: number;
  currentMileage: number;
  purchasingPrice?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Driver {
  id: number;
  fullName: string;
  licenseNumber: string;
  licenseExpiryDate?: string;
  phoneNumber: string;
  email?: string;
  active: boolean;
  userId?: number;
  username?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Route {
  id: number;
  origin: string;
  destination: string;
  officialDistanceKm: number;
  roundTrip: boolean;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Trip {
  id: number;
  tripNumber: string;
  vehicleId: number;
  vehicleRegistration: string;
  driverId: number;
  driverName: string;
  routeId?: number;
  origin: string;
  destination: string;
  purpose: string;
  jobReference?: string;
  startDate: string;
  endDate?: string;
  startOdometerReading?: number;
  endOdometerReading?: number;
  actualDistanceKm?: number;
  plannedDistanceKm: number;
  weightInTons?: number;
  ratePerKm?: number;
  ratePerTon?: number;
  amount?: number;
  fixedPrice?: number;
  distanceOverridden: boolean;
  distanceOverrideReason?: string;
  status: TripStatus;
  createdAt: string;
  updatedAt: string;
}

export interface FuelRecord {
  id: number;
  tripId: number;
  tripNumber: string;
  vehicleId: number;
  vehicleRegistration: string;
  fuelDate: string;
  quantityLiters: number;
  costPerLiter: number;
  totalCost: number;
  transactionType: TransactionType;
  expectedConsumptionLiters?: number;
  varianceLiters?: number;
  variancePercentage?: number;
  flaggedForReview: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Allowance {
  id: number;
  tripId: number;
  tripNumber: string;
  driverId: number;
  driverName: string;
  type: AllowanceType | string;
  amount: number;
  status: AllowanceStatus;
  approvedByUsername?: string;
  approvedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  tripId: number;
  tripNumber: string;
  invoiceDate: string;
  distanceKm: number;
  ratePerKm: number;
  distanceCost: number;
  fuelCost: number;
  allowanceCost: number;
  adjustmentAmount: number;
  adjustmentReason?: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  status: InvoiceStatus;
  clientName: string;
  clientReference?: string;
  notes?: string;
  generatedByUsername: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLog {
  id: number;
  timestamp: string;
  username: string;
  action: string;
  entityType: string;
  entityId: number;
  details: string;
  ipAddress: string;
}

export interface DashboardMetrics {
  totalActiveVehicles: number;
  activeTripsToday: number;
  pendingAllowances: number;
  draftInvoices: number;
  flaggedFuelRecords: number;
}

export interface ApiError {
  status: number;
  message: string;
  timestamp: string;
}

export interface PaginationParams {
  page?: number;
  size?: number;
  sort?: string;
}

export interface CreateVehicleRequest {
  registrationNumber: string;
  make: string;
  model: string;
  year: number;
  fuelType: FuelType;
  fuelEfficiencyKmPerLiter: number;
  currentMileage: number;
  purchasingPrice?: number;
}

export interface CreateDriverRequest {
  fullName: string;
  licenseNumber: string;
  phoneNumber: string;
  email?: string;
  userId?: number;
}

export interface CreateRouteRequest {
  origin: string;
  destination: string;
  officialDistanceKm?: number;
  roundTrip: boolean;
  description?: string;
}

export interface CreateTripRequest {
  vehicleId: number;
  driverId: number;
  routeId?: number;
  origin: string;
  destination: string;
  purpose: string;
  jobReference?: string;
  startDate: string;
  endDate?: string;
  plannedDistanceKm?: number;
  weightInTons?: number;
}

export interface RecordFuelRequest {
  tripId: number;
  vehicleId: number;
  fuelDate: string;
  quantityLiters: number;
  costPerLiter: number;
  transactionType: TransactionType;
  notes?: string;
}

export interface ProFormaInvoice {
  id: number;
  proFormaNumber: string;
  issueDate: string;
  origin: string;
  destination: string;
  distanceKm: number;
  ratePerKm: number;
  distanceCost: number;
  additionalCharges: number;
  additionalChargesDescription?: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  status: ProFormaStatus;
  clientName: string;
  clientContact?: string;
  clientReference?: string;
  validUntil: string;
  notes?: string;
  generatedByUsername: string;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: number;
  paymentReference: string;
  invoiceId: number;
  invoiceNumber: string;
  paymentDate: string;
  amount: number;
  currency: string;
  paymentMethod: PaymentMethod;
  transactionReference?: string;
  payerName: string;
  payerContact?: string;
  status: PaymentStatus;
  notes?: string;
  recordedByUsername: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAllowanceRequest {
  tripId: number;
  type: AllowanceType | string;
  amount: number;
  notes?: string;
}

export interface GenerateInvoiceRequest {
  tripId: number;
  ratePerKm: number;
  adjustmentAmount?: number;
  adjustmentReason?: string;
  taxAmount?: number;
  clientName: string;
  clientReference?: string;
  notes?: string;
}

export interface CreateProFormaInvoiceRequest {
  origin: string;
  destination: string;
  distanceKm: number;
  ratePerKm: number;
  additionalCharges: number;
  additionalChargesDescription?: string;
  taxAmount: number;
  clientName: string;
  clientContact?: string;
  clientReference?: string;
  validUntil: string;
  notes?: string;
}

export interface RecordPaymentRequest {
  invoiceId: number;
  paymentDate: string;
  amount: number;
  paymentMethod: PaymentMethod;
  transactionReference?: string;
  payerName: string;
  payerContact?: string;
  notes?: string;
}

export interface CreateUserRequest {
  username: string;
  email?: string;
  password?: string;
  fullName: string;
  roles: UserRole[];
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface TripFinanceReport {
  tripId: number;
  tripNumber: string;
  startDate: string;
  endDate?: string;
  origin: string;
  destination: string;
  vehicleRegistration: string;
  driverName: string;
  invoiceAmount: number;
  invoiceNumber?: string;
  invoiceStatus?: InvoiceStatus;
  totalPayments: number;
  outstandingBalance: number;
  totalAllowances: number;
  approvedAllowances: number;
  pendingAllowances: number;
  netProfit: number;
}

export interface MonthlyFinanceReport {
  year: number;
  month: number;
  monthName: string;
  totalTrips: number;
  completedTrips: number;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalAllowances: number;
  approvedAllowances: number;
  pendingAllowances: number;
  totalRevenue: number;
  netProfit: number;
  trips: TripFinanceReport[];
}

export interface YearlyFinanceReport {
  year: number;
  totalTrips: number;
  completedTrips: number;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalAllowances: number;
  approvedAllowances: number;
  pendingAllowances: number;
  totalRevenue: number;
  netProfit: number;
  monthlyReports: MonthlyFinanceReport[];
}

export interface FinanceSummary {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalAllowances: number;
  netProfit: number;
  invoiceCount: number;
  paymentCount: number;
}

export enum ServiceType {
  ROUTINE_MAINTENANCE = 'ROUTINE_MAINTENANCE',
  OIL_CHANGE = 'OIL_CHANGE',
  TIRE_REPLACEMENT = 'TIRE_REPLACEMENT',
  BRAKE_SERVICE = 'BRAKE_SERVICE',
  ENGINE_REPAIR = 'ENGINE_REPAIR',
  TRANSMISSION_REPAIR = 'TRANSMISSION_REPAIR',
  ELECTRICAL_REPAIR = 'ELECTRICAL_REPAIR',
  BODY_WORK = 'BODY_WORK',
  INSPECTION = 'INSPECTION',
  EMERGENCY_REPAIR = 'EMERGENCY_REPAIR',
  OTHER = 'OTHER',
}

export enum ServiceStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum ExpenseCategory {
  // Vehicle expenses
  FUEL = 'FUEL',
  MAINTENANCE = 'MAINTENANCE',
  REPAIR = 'REPAIR',
  INSURANCE = 'INSURANCE',
  LATRA = 'LATRA',
  REGISTRATION = 'REGISTRATION',
  PARKING = 'PARKING',
  TOLLS = 'TOLLS',
  CLEANING = 'CLEANING',
  TIRES = 'TIRES',
  PARTS = 'PARTS',
  LABOR = 'LABOR',
  INSPECTION = 'INSPECTION',
  FINES = 'FINES',
  // Trip-related expenses/allowances
  DRIVER_ALLOWANCE = 'DRIVER_ALLOWANCE',
  ACCOMMODATION = 'ACCOMMODATION',
  MEALS = 'MEALS',
  TOLL_ROAD = 'TOLL_ROAD',
  LOADING_OFFLOADING = 'LOADING_OFFLOADING',
  WEIGHBRIDGE = 'WEIGHBRIDGE',
  BORDER_FEES = 'BORDER_FEES',
  FUEL_ADVANCE = 'FUEL_ADVANCE',
  OTHER = 'OTHER',
}

export enum ExpenseStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PAID = 'PAID',
}

export interface VehicleService {
  id: number;
  serviceNumber: string;
  vehicleId: number;
  vehicleRegistration: string;
  serviceDate: string;
  serviceType: ServiceType;
  odometerReading?: number;
  laborCost: number;
  partsCost: number;
  totalCost: number;
  serviceProvider: string;
  serviceProviderContact?: string;
  workPerformed?: string;
  partsReplaced?: string;
  nextServiceDue?: string;
  nextServiceOdometer?: number;
  status: ServiceStatus;
  invoiceReference?: string;
  notes?: string;
  recordedByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleExpense {
  id: number;
  expenseNumber: string;
  vehicleId: number;
  vehicleRegistration: string;
  tripId?: number;
  tripNumber?: string;
  expenseDate: string;
  category: ExpenseCategory;
  amount: number;
  currency: string;
  vendor?: string;
  receiptNumber?: string;
  description?: string;
  reimbursable: boolean;
  status: ExpenseStatus;
  approvedByName?: string;
  approvedAt?: string;
  notes?: string;
  recordedByName?: string;
  createdAt: string;
  updatedAt?: string;
  // Maintenance-specific fields
  odometerReading?: number;
  serviceProvider?: string;
  serviceProviderContact?: string;
  workPerformed?: string;
  partsReplaced?: string;
  nextServiceDue?: string;
  nextServiceOdometer?: number;
  // Insurance/LATRA specific fields
  validFrom?: string;
  validUntil?: string;
  policyNumber?: string;
  insuranceProvider?: string;
  // Attachment fields
  attachmentFileName?: string;
  attachmentFilePath?: string;
  attachmentContentType?: string;
  hasAttachment?: boolean;
}

export interface VehicleROIReport {
  vehicleId: number;
  registrationNumber: string;
  make: string;
  model: string;
  year: number;
  reportStartDate: string;
  reportEndDate: string;
  totalTrips: number;
  completedTrips: number;
  totalDistanceKm: number;
  totalRevenue: number;
  totalInvoiced: number;
  totalPaid: number;
  outstandingAmount: number;
  totalExpenses: number;
  fuelExpenses: number;
  maintenanceExpenses: number;
  repairExpenses: number;
  otherExpenses: number;
  totalAllowances: number;
  grossProfit: number;
  netProfit: number;
  profitMargin: number;
  roi: number;
  revenuePerKm: number;
  expensePerKm: number;
  profitPerKm: number;
  purchasingPrice?: number;
  totalCostOfOwnership: number;
  estimatedDepreciation: number;
  roiIncludingDepreciation: number;
  breakEvenRevenue: number;
  trips: TripFinanceReport[];
  expenses: VehicleExpense[];
  services: VehicleService[];
}

export interface VehicleROISummary {
  vehicleId: number;
  registrationNumber: string;
  make: string;
  model: string;
  totalTrips: number;
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  roi: number;
  averageRevenuePerTrip: number;
  averageExpensePerTrip: number;
  averageProfitPerTrip: number;
  purchasingPrice?: number;
  totalCostOfOwnership: number;
  roiIncludingDepreciation: number;
}

export interface RecordServiceRequest {
  vehicleId: number;
  serviceDate: string;
  serviceType: ServiceType;
  odometerReading?: number;
  laborCost: number;
  partsCost: number;
  serviceProvider: string;
  serviceProviderContact?: string;
  workPerformed?: string;
  partsReplaced?: string;
  nextServiceDue?: string;
  nextServiceOdometer?: number;
  invoiceReference?: string;
  notes?: string;
}

export interface RecordExpenseRequest {
  vehicleId: number;
  tripId?: number;
  expenseDate: string;
  category: ExpenseCategory;
  amount: number;
  vendor?: string;
  receiptNumber?: string;
  description?: string;
  reimbursable?: boolean;
  notes?: string;
  // Maintenance-specific fields
  odometerReading?: number;
  serviceProvider?: string;
  serviceProviderContact?: string;
  workPerformed?: string;
  partsReplaced?: string;
  nextServiceDue?: string;
  nextServiceOdometer?: number;
  // Insurance/LATRA specific fields
  validFrom?: string;
  validUntil?: string;
  policyNumber?: string;
  insuranceProvider?: string;
  // Attachment fields
  attachmentFileName?: string;
  attachmentFilePath?: string;
  attachmentContentType?: string;
}

// Contract Management Types
export enum ContractType {
  TENDER = 'TENDER',
  REGULAR = 'REGULAR',
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export enum ContractStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  ON_HOLD = 'ON_HOLD',
}

export interface Contract {
  id: number;
  contractNumber: string;
  contractName: string;
  clientName: string;
  clientContact?: string;
  clientReference?: string;
  contractType: ContractType;
  status: ContractStatus;
  description?: string;
  totalWeightTons: number;
  completedWeightTons: number;
  totalDistanceKm?: number;
  origin: string;
  destination: string;
  contractValue: number;
  ratePerTon?: number;
  ratePerKm?: number;
  currency: string;
  paymentTerms?: string;
  startDate: string;
  expectedCompletionDate?: string;
  endDate?: string;
  actualCompletionDate?: string;
  specialConditions?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface ContractSummary {
  id: number;
  contractNumber: string;
  contractName: string;
  clientName: string;
  contractType: ContractType;
  status: ContractStatus;
  totalWeightTons: number;
  completedWeightTons: number;
  contractValue: number;
  completionPercentage: number;
  totalTrips: number;
  completedTrips: number;
  totalInvoiced: number;
  totalPaid: number;
  outstandingAmount: number;
}

export interface ContractProgress {
  contractId: number;
  weightProgress: {
    total: number;
    completed: number;
    remaining: number;
    percentage: number;
  };
  tripProgress: {
    total: number;
    completed: number;
    inProgress: number;
    planned: number;
    percentage: number;
  };
  timeProgress: {
    startDate: string;
    expectedCompletionDate?: string;
    daysElapsed: number;
    totalDays?: number;
    percentage?: number;
    status: 'ON_SCHEDULE' | 'BEHIND_SCHEDULE' | 'AHEAD_OF_SCHEDULE';
  };
  financialProgress: {
    contractValue: number;
    invoiced: number;
    paid: number;
    outstanding: number;
    remainingToInvoice: number;
    percentageInvoiced: number;
    percentagePaid: number;
  };
}

export interface ContractProfitability {
  contractId: number;
  contractNumber: string;
  contractName: string;
  revenue: {
    contractValue: number;
    invoiced: number;
    paid: number;
    outstanding: number;
  };
  expenses: {
    total: number;
    fuel: number;
    maintenance: number;
    allowances: number;
    other: number;
  };
  profitability: {
    grossProfit: number;
    netProfit: number;
    profitMargin: number;
    roi: number;
  };
}

export interface CreateContractRequest {
  contractName: string;
  clientName: string;
  clientContact?: string;
  clientReference?: string;
  contractType: ContractType;
  description?: string;
  totalWeightTons: number;
  totalDistanceKm?: number;
  origin: string;
  destination: string;
  contractValue: number;
  ratePerTon?: number;
  ratePerKm?: number;
  currency?: string;
  paymentTerms?: string;
  startDate: string;
  expectedCompletionDate?: string;
  endDate?: string;
  specialConditions?: string;
  notes?: string;
}

export interface UpdateContractRequest extends Partial<CreateContractRequest> {
  status?: ContractStatus;
}

// Invoice Line Items Types
export interface InvoiceLineItem {
  id: number;
  invoiceId: number;
  tripId: number;
  lineNumber: number;
  description: string;
  weightTons: number;
  distanceKm: number;
  ratePerTon: number;
  ratePerKm: number;
  weightCost: number;
  distanceCost: number;
  fuelCost: number;
  allowanceCost: number;
  otherCharges: number;
  discountPercentage: number;
  discountAmount: number;
  lineTotal: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInvoiceLineItemRequest {
  tripId: number;
  description: string;
  weightTons: number;
  distanceKm: number;
  ratePerTon: number;
  ratePerKm: number;
  fuelCost?: number;
  allowanceCost?: number;
  otherCharges?: number;
  discountPercentage?: number;
  discountAmount?: number;
}

export interface InvoiceWithLineItems {
  id: number;
  invoiceNumber: string;
  contractId?: number;
  invoiceDate: string;
  clientName: string;
  clientReference?: string;
  status: string;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  invoiceDiscount: number;
  adjustmentAmount: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  paymentTerms?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMultiTripInvoiceRequest {
  contractId?: number;
  invoiceDate: string;
  clientName: string;
  clientReference?: string;
  lineItems: CreateInvoiceLineItemRequest[];
  invoiceDiscount?: number;
  invoiceDiscountReason?: string;
  adjustmentAmount?: number;
  adjustmentReason?: string;
  taxAmount?: number;
  paymentTerms?: string;
  notes?: string;
}

// Updated Trip interface to include contract
export interface TripWithContract extends Trip {
  contractId?: number;
  contractNumber?: string;
  contractName?: string;
}

// Payroll Types
export enum EmployeeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  TERMINATED = 'TERMINATED',
}

export enum PayrollStatus {
  DRAFT = 'DRAFT',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export interface Employee {
  id: number;
  employeeNumber: string;
  fullName: string;
  position?: string;
  department?: string;
  phoneNumber?: string;
  email?: string;
  basicSalary: number;
  hireDate?: string;
  status: EmployeeStatus;
  driverId?: number;
  driverName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmployeeRequest {
  fullName: string;
  position?: string;
  department?: string;
  phoneNumber?: string;
  email?: string;
  basicSalary: number;
  hireDate?: string;
  driverId?: number;
}

export interface AllowanceItem {
  name: string;
  amount: number;
}

export interface DeductionItem {
  name: string;
  amount: number;
}

export interface PayrollRecord {
  id: number;
  payrollNumber: string;
  userId: number;
  userName: string;
  userEmail?: string;
  payrollYear: number;
  payrollMonth: number;
  payrollPeriod: string;
  basicSalary: number;
  totalAllowances: number;
  totalDeductions: number;
  grossPay: number;
  netPay: number;
  allowancesBreakdown?: string;
  deductionsBreakdown?: string;
  status: PayrollStatus;
  notes?: string;
  createdByName?: string;
  approvedByName?: string;
  approvedAt?: string;
  paidByName?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePayrollRequest {
  userId: number;
  payrollYear: number;
  payrollMonth: number;
  basicSalary?: number;
  allowances?: AllowanceItem[];
  deductions?: DeductionItem[];
  notes?: string;
}

// Trip Allowance Types
export enum TripAllowanceType {
  DRIVER_ALLOWANCE = 'DRIVER_ALLOWANCE',
  ACCOMMODATION = 'ACCOMMODATION',
  MEAL = 'MEAL',
  FUEL_ADVANCE = 'FUEL_ADVANCE',
  TOLL_FEES = 'TOLL_FEES',
  PARKING = 'PARKING',
  OTHER = 'OTHER',
}

export enum TripExpenseType {
  FUEL = 'FUEL',
  TOLL_ROAD = 'TOLL_ROAD',
  PARKING = 'PARKING',
  REPAIRS = 'REPAIRS',
  MEALS = 'MEALS',
  ACCOMMODATION = 'ACCOMMODATION',
  LOADING_OFFLOADING = 'LOADING_OFFLOADING',
  WEIGHBRIDGE = 'WEIGHBRIDGE',
  BORDER_FEES = 'BORDER_FEES',
  OTHER = 'OTHER',
}

export interface TripAllowance {
  id: number;
  tripId: number;
  tripNumber: string;
  type: TripAllowanceType;
  amount: number;
  description?: string;
  recordedByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTripAllowanceRequest {
  tripId: number;
  type: TripAllowanceType;
  amount: number;
  description?: string;
}

export interface TripExpenseItem {
  id: number;
  tripId: number;
  tripNumber: string;
  type: TripExpenseType;
  amount: number;
  expenseDate?: string;
  description?: string;
  vendor?: string;
  receiptNumber?: string;
  recordedByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTripExpenseRequest {
  tripId: number;
  type: TripExpenseType;
  amount: number;
  expenseDate?: string;
  description?: string;
  vendor?: string;
  receiptNumber?: string;
}

export interface TripFinancialSummary {
  tripId: number;
  tripNumber: string;
  fixedPrice: number;
  totalAllowances: number;
  totalExpenses: number;
  remainingBalance: number;
  isOverBudget: boolean;
  allowances: VehicleExpense[];
  expenses: VehicleExpense[];
}
